import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {EmployeeRoleLevel, RedactedWorkstreamMetric, WorkstreamMetric} from 'client';
import {Column, Table} from 'components/DSYS/Table';
import {formatCompactMetricValue, getMetricDisplayValue} from 'helpers/compactMetricValue';
import {t} from 'i18next';
import {Fragment, useMemo, useState} from 'react';
import {isFullWorkstreamMetric} from 'views/workstreams/utils';

import {
  METRIC_FORECAST_PERIODS,
  MetricForecastGrid,
  MetricForecastHeader,
} from './MetricForecastGrid';
import {RedactedMetricValuePlaceholder} from './RedactedMetricValuePlaceholder';
import styles from './WorkstreamMetricsAnnualTable.module.css';
import {type DetailsTableMetric, SubWorkstreamHeaderRow} from './WorkstreamMetricsQuarterTable';
import {
  EditableMetricValue,
  getRedactedMetricIdentityProjection,
  MetricStatusControl,
  type WorkstreamMetricChangeHandler,
  WorkstreamMetricNameCellContent,
} from './WorkstreamRedesignSections';

type AnnualTableMetric = DetailsTableMetric | RedactedWorkstreamMetric;

export const ANNUAL_METRIC_COLUMNS: Column<AnnualTableMetric>[] = [
  {type: 'metric', minWidth: 240, spanner: true},
  {type: 'status', minWidth: 112, width: 132},
  {type: 'baseline', minWidth: 96, width: 120},
  {type: 'current', minWidth: 96, width: 120},
  {type: 'forecast', minWidth: 356, width: 420},
];

const annualHeaderLabel = (label: string) => (
  <Text isBold size="small">
    {label}
  </Text>
);

/** Column header labels for every annual metrics table. */
export const getAnnualColumnHeaderLabel = () => ({
  metric: annualHeaderLabel(t('metric').toString()),
  status: annualHeaderLabel(t('status').toString()),
  baseline: annualHeaderLabel(t('baseline').toString()),
  current: annualHeaderLabel(t('current').toString()),
  forecast: <MetricForecastHeader className={styles.annualForecastHeader} />,
});

/** Sets the annual value type scale; put it on any table that renders {@link AnnualMetricRow}. */
export const ANNUAL_TABLE_CLASS_NAME = styles.annualTable;

const getForecast = (metric: WorkstreamMetric, period: string) =>
  metric.metric_forecasts?.find((forecast) => forecast.period === period);

const MetricValue = ({
  className,
  valueClassName,
  value,
}: {
  className?: string;
  valueClassName?: string;
  value: string | null | undefined;
}) => (
  <span className={className}>
    <span className={valueClassName}>
      {formatCompactMetricValue(getMetricDisplayValue(value)).displayValue}
    </span>
  </span>
);

/** A single annual metric row, shared by the details table and the grouped scorecard. */
export const AnnualMetricRow = ({
  handleMetricChange,
  metric,
  owner,
  statusChangeSource,
  workstreamId,
  year,
}: {
  handleMetricChange?: WorkstreamMetricChangeHandler;
  metric: AnnualTableMetric;
  owner?: EmployeeRoleLevel;
  statusChangeSource?: 'details_inline_annual';
  workstreamId: number;
  year: number;
}) => {
  const fullMetric = isFullWorkstreamMetric(metric) ? metric : null;
  const identityMetric =
    fullMetric ?? getRedactedMetricIdentityProjection(metric as RedactedWorkstreamMetric);
  const renderRedactedValueCell = (key: number) => (
    <Table.Cell key={key} style={{padding: 8}}>
      <Box display="flex" alignItems="flex-start">
        <RedactedMetricValuePlaceholder />
      </Box>
    </Table.Cell>
  );
  const renderRedactedForecastCell = () => (
    <Table.Cell style={{padding: 8}}>
      <div className={styles.annualRedactedForecastGrid}>
        <div aria-hidden />
        {METRIC_FORECAST_PERIODS.map((period) => (
          <RedactedMetricValuePlaceholder key={period} centerHorizontally />
        ))}
      </div>
    </Table.Cell>
  );

  return (
    <Table.Row>
      <Table.Cell style={{padding: 8}}>
        <Box display="flex" style={{gap: 4}}>
          <WorkstreamMetricNameCellContent
            metric={identityMetric}
            owner={owner}
            redactedMetric={!fullMetric}
            showPrimaryMetricIcon={Boolean(fullMetric)}
            workstreamId={fullMetric ? workstreamId : undefined}
          />
        </Box>
      </Table.Cell>
      {fullMetric ? (
        <>
          <Table.Cell style={{padding: 8}}>
            <Box display="inline-flex">
              <MetricStatusControl
                metric={fullMetric}
                handleMetricChange={handleMetricChange}
                statusChangeSource={statusChangeSource}
                workstreamId={workstreamId}
              />
            </Box>
          </Table.Cell>
          <Table.Cell style={{padding: 8, textAlign: 'center'}}>
            <MetricValue
              className={styles.annualMetricValue}
              valueClassName={styles.annualValueText}
              value={fullMetric.baseline}
            />
          </Table.Cell>
          <Table.Cell style={{padding: 8}}>
            <Box className={styles.annualCurrentCell}>
              {handleMetricChange ? (
                <EditableMetricValue
                  className={styles.annualCurrentValue}
                  metric={fullMetric}
                  metricTitleText={fullMetric.title}
                  column="current"
                  handleMetricChange={handleMetricChange}
                />
              ) : (
                <span className={styles.annualValueText}>
                  {formatCompactMetricValue(getMetricDisplayValue(fullMetric.current)).displayValue}
                </span>
              )}
            </Box>
          </Table.Cell>
          <Table.Cell style={{padding: 8}}>
            <MetricForecastGrid
              year={year}
              className={styles.annualForecastGrid}
              labelClassName={styles.annualForecastRowLabel}
              rowClassName={styles.annualForecastRow}
              renderCell={({field, period}) => (
                <MetricValue
                  className={styles.annualForecastValue}
                  valueClassName={styles.annualForecastText}
                  value={getForecast(fullMetric, period)?.[field] ?? null}
                />
              )}
            />
          </Table.Cell>
        </>
      ) : (
        <>
          {Array.from({length: ANNUAL_METRIC_COLUMNS.length - 2}, (_, index) =>
            renderRedactedValueCell(index)
          )}
          {renderRedactedForecastCell()}
        </>
      )}
    </Table.Row>
  );
};

export type AnnualMetricSubGroup = {
  id: number;
  name: string;
  metrics: AnnualTableMetric[];
};

/**
 * Annual counterpart to the quarterly grouped table. Using the shared table primitive
 * keeps every forecast column reachable through its horizontal scroll container.
 */
export const WorkstreamMetricsAnnualGroupedTable = ({
  handleMetricChange,
  metrics,
  owner,
  statusChangeSource,
  subGroups,
  subWorkstreamsExpanded: controlledSubWorkstreamsExpanded,
  onSubWorkstreamsExpandedChange,
  workstreamId,
  year,
}: {
  handleMetricChange?: WorkstreamMetricChangeHandler;
  metrics: AnnualTableMetric[];
  owner?: EmployeeRoleLevel;
  statusChangeSource?: 'details_inline_annual';
  subGroups: AnnualMetricSubGroup[];
  subWorkstreamsExpanded?: boolean;
  onSubWorkstreamsExpandedChange?: (expanded: boolean) => void;
  workstreamId: number;
  year: number;
}) => {
  const [uncontrolledSubWorkstreamsExpanded, setUncontrolledSubWorkstreamsExpanded] =
    useState(false);
  const subWorkstreamsExpanded =
    controlledSubWorkstreamsExpanded ?? uncontrolledSubWorkstreamsExpanded;
  const toggleSubWorkstreams = () => {
    const nextExpanded = !subWorkstreamsExpanded;
    setUncontrolledSubWorkstreamsExpanded(nextExpanded);
    onSubWorkstreamsExpandedChange?.(nextExpanded);
  };
  const columnHeaderLabel = useMemo(() => getAnnualColumnHeaderLabel(), []);
  const flatData = useMemo(
    () => [...metrics, ...subGroups.flatMap((group) => group.metrics)],
    [metrics, subGroups]
  );

  return (
    <Table
      columns={ANNUAL_METRIC_COLUMNS}
      data={flatData}
      bodyCellPadding={8}
      border={false}
      className={styles.annualTable}
      horizontalLines
      resizable={false}
      columnHeaderLabel={columnHeaderLabel}
      style={{width: '100%'}}
    >
      <Table.Header />
      <Table.Body>
        {metrics.map((metric) => (
          <AnnualMetricRow
            key={metric.id}
            metric={metric}
            year={year}
            workstreamId={workstreamId}
            owner={owner}
            handleMetricChange={handleMetricChange}
            statusChangeSource={statusChangeSource}
          />
        ))}
        {subGroups.length > 0 ? (
          <SubWorkstreamHeaderRow
            name={t('sub_workstreams').toString()}
            expanded={subWorkstreamsExpanded}
            onToggle={toggleSubWorkstreams}
            columnCount={ANNUAL_METRIC_COLUMNS.length}
          />
        ) : null}
        {subWorkstreamsExpanded
          ? subGroups.map((group) => (
              <Fragment key={group.id}>
                <SubWorkstreamHeaderRow
                  name={group.name}
                  columnCount={ANNUAL_METRIC_COLUMNS.length}
                />
                {group.metrics.map((metric) => (
                  <AnnualMetricRow
                    key={metric.id}
                    metric={metric}
                    year={year}
                    workstreamId={group.id}
                  />
                ))}
              </Fragment>
            ))
          : null}
      </Table.Body>
    </Table>
  );
};
